<template>
  <v-card>
    <v-card-text class="pa-4">
      <div class="field-test-charge__title">{{ $t('onboarding.field.test-charge.title') }}</div>
      <p v-if="simulation" class="text-body-2 mb-2">
        <v-icon small color="success" class="mr-1">mdi-flask</v-icon>{{ $t('onboarding.field.test-charge.as-member', { member: simulation.memberName, minutes: intervention.testChargeCap.minutes, energy: intervention.testChargeCap.energyWh / 1000 }) }}
      </p>
      <template v-else-if="canTechnical">
        <p class="text-body-2 text--secondary mb-2">{{ $t('onboarding.field.test-charge.technical-hint') }}</p>
        <v-textarea v-model="technicalReason" :label="$t('onboarding.field.test-charge.reason')" outlined dense rows="2" auto-grow hide-details class="mb-2" :disabled="running" />
      </template>
      <p v-else class="text-body-2 text--secondary mb-2">{{ $t('onboarding.field.test-charge.needs-simulation') }}</p>

      <!-- Le point de charge à essayer, quand la borne en a plusieurs. -->
      <v-chip-group v-if="evses.length > 1" v-model="evseId" mandatory column class="mb-1">
        <v-chip v-for="evse in evses" :key="evse.externalId" :value="evse.externalId" small filter outlined :disabled="running">
          {{ evse.evseId || evse.externalId }}
        </v-chip>
      </v-chip-group>

      <div class="d-flex align-center">
        <v-btn v-if="!running" small depressed color="primary" :disabled="!canManage || !evseId || !canStart" :loading="busy" @click="start">
          <v-icon left small>mdi-play</v-icon>{{ $t(state === 'idle' ? 'onboarding.field.test-charge.start' : 'onboarding.field.test-charge.again') }}
        </v-btn>
        <v-btn v-else small outlined color="primary" :disabled="stopping" :loading="busy" @click="stop">
          <v-icon left small>mdi-stop</v-icon>{{ $t('onboarding.field.test-charge.stop') }}
        </v-btn>
      </div>

      <!-- Où en est la charge : l'ordre accepté, la charge commencée, ce qu'elle délivre, arrêtée. -->
      <v-alert v-if="state !== 'idle'" :type="alertType" text dense class="mt-3 mb-0 text-body-2">
        <div class="d-flex align-center">
          <v-progress-circular v-if="running" indeterminate size="16" width="2" class="mr-2" />
          <span class="font-weight-medium">{{ $t(`onboarding.field.test-charge.state.${state}`) }}</span>
        </div>
        <div v-if="session">
          {{ $t('onboarding.field.test-charge.delivered', { energy: energyLabel, duration: durationLabel }) }}
        </div>
        <div v-if="reason" class="text--secondary">{{ reason }}</div>
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'

const POLL_MS = 3000
/** Au-delà, la borne n'a pas répondu ou la charge n'a pas commencé : le câble n'est sans doute pas branché. */
const ANSWER_TIMEOUT_MS = 60000
const START_TIMEOUT_MS = 90000

/**
 * La charge d'essai : la borne démarre une charge à distance sur un point de charge, on la voit commencer et délivrer
 * de l'énergie, puis on l'arrête. Il faut un véhicule ou un boîtier de test branché. En mode test avec un membre simulé,
 * c'est une charge de test à son nom, jugée par ses règles ; sinon, avec le rôle Superviseur, une charge technique sans
 * membre, avec son motif (gratuite, plafonnée).
 */
export default {
  name: 'FieldTestCharge',

  mixins: [CommissioningMixin],

  props: {
    chargingPointId: { type: String, required: true },
    evses: { type: Array, required: true },
    canManage: { type: Boolean, default: false },
    // En mode test avec un membre simulé, la charge se lance à son nom (charge de test, gratuite et plafonnée).
    intervention: { type: Object, default: null },
    // Sans membre simulé : une charge technique, avec son motif (rôle Superviseur).
    canTechnical: { type: Boolean, default: false },
  },

  data() {
    return {
      evseId: this.evses[0]?.externalId || null,
      // idle, sending, waiting, charging, stopping, done, refused, not-started, error
      state: 'idle',
      reason: null,
      session: null,
      since: null,
      startedAt: null,
      busy: false,
      timer: null,
      technicalReason: '',
    }
  },

  computed: {
    simulation() {
      return this.intervention?.testMode ? this.intervention.simulation : null
    },

    canStart() {
      return !!this.simulation || (this.canTechnical && !!this.technicalReason.trim())
    },

    running() {
      return ['sending', 'waiting', 'charging', 'stopping'].includes(this.state)
    },

    stopping() {
      return this.state === 'stopping'
    },

    alertType() {
      return { done: 'success', refused: 'error', error: 'error', 'not-started': 'warning' }[this.state] || 'info'
    },

    energyLabel() {
      return `${((this.session?.energyWh || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} kWh`
    },

    durationLabel() {
      const seconds = this.session?.durationSeconds || 0
      return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`
    },
  },

  beforeDestroy() {
    clearTimeout(this.timer)
  },

  methods: {
    async start() {
      clearTimeout(this.timer)
      Object.assign(this, { state: 'sending', reason: null, session: null, busy: true })
      try {
        const result = this.simulation
          ? await FieldRepository.startTestCharge(this.intervention.id, this.evseId)
          : await FieldRepository.sendAction(this.chargingPointId, 'remote-start-session', { evseId: this.evseId, reason: this.technicalReason.trim() })
        this.startedAt = Date.now()
        this.awaitAnswer(result.messageIds[0])
      } catch (e) {
        this.fail(e)
      } finally {
        this.busy = false
      }
    },

    /** D'abord la réponse de la borne à l'ordre : acceptée, on attend la charge ; refusée, on le dit. */
    awaitAnswer(messageId) {
      this.timer = setTimeout(async () => {
        try {
          const outcome = await FieldRepository.actionOutcome(this.chargingPointId, messageId)
          if (outcome.state === 'answered') {
            if (outcome.response?.status && outcome.response.status !== 'Accepted') {
              Object.assign(this, { state: 'refused', reason: outcome.response.status })
              return
            }
            this.since = outcome.sentAt
            this.state = 'waiting'
            this.follow()
            return
          }
          if (outcome.state === 'error') {
            Object.assign(this, { state: 'refused', reason: outcome.error?.code || null })
            return
          }
        } catch (e) {
          // Réseau coupé un instant : on réessaie au tour suivant.
        }
        if (Date.now() - this.startedAt > ANSWER_TIMEOUT_MS) {
          Object.assign(this, { state: 'error', reason: this.$t('onboarding.field.maintenance.no-answer') })
          return
        }
        this.awaitAnswer(messageId)
      }, POLL_MS)
    },

    /** Puis la charge elle-même, jusqu'à ce qu'elle soit terminée. */
    follow() {
      this.timer = setTimeout(async () => {
        try {
          const { session } = await FieldRepository.testCharge(this.chargingPointId, this.evseId, this.since)
          this.session = session
          if (session && ['COMPLETED', 'INVALID', 'CANCELED'].includes(session.status)) {
            Object.assign(this, { state: session.status === 'COMPLETED' ? 'done' : 'refused', reason: session.stopReason || null })
            return
          }
          if (session && this.state === 'waiting') {
            this.state = 'charging'
          }
        } catch (e) {
          // Réseau coupé un instant : on réessaie au tour suivant.
        }
        if (!this.session && Date.now() - this.startedAt > START_TIMEOUT_MS) {
          this.state = 'not-started'
          return
        }
        this.follow()
      }, POLL_MS)
    },

    async stop() {
      this.busy = true
      try {
        await FieldRepository.sendAction(this.chargingPointId, 'remote-stop-session', { evseId: this.evseId })
        this.state = 'stopping'
      } catch (e) {
        this.reason = e?.response ? (e.response.data?.reason || this.messageOf(e)) : this.$t('onboarding.field.offline-action')
      } finally {
        this.busy = false
      }
    },

    fail(e) {
      Object.assign(this, { state: 'error', reason: e?.response ? (e.response.data?.reason || this.messageOf(e)) : this.$t('onboarding.field.offline-action') })
    },
  },
}
</script>

<style scoped>
.field-test-charge__title {
  font-weight: 500;
  font-size: 1.05rem;
  margin-bottom: 8px;
}
</style>
